import { message, Modal } from 'antd';
import { GlossaryNode, GlossaryTerm } from '../../../../types.generated';
import { ChildGlossaryTermFragment } from '../../../../graphql/glossaryNode.generated';
import { GlossaryNodeFragment } from '../../../../graphql/fragments.generated';

interface BulkDeleteGlossaryEntitiesParams {
    nodes: (GlossaryNode | GlossaryNodeFragment)[];
    terms: (GlossaryTerm | ChildGlossaryTermFragment)[];
    batchRemoveGlossaryMutation: any;
    selectedItems: string[];
    onSuccess?: () => void;
    refetch?: () => Promise<any>;
    setSelectedItems: (items: string[]) => void;
    setIsDeleting?: (isDeleting: boolean) => void;
}

/**
 * Handles the bulk deletion of the selected glossary terms.
 * Uses the batchRemoveGlossaryMutation to delete each of the selected glossary terms.
 * Displays a notification message to the user once the deletion is completed.
 * Clears the selectedItems state after completing the operation.
 */
export const bulkDeleteGlossaryEntities = ({
    nodes,
    terms,
    selectedItems,
    batchRemoveGlossaryMutation,
    setSelectedItems,
    setIsDeleting,
    refetch,
    onSuccess,
}: BulkDeleteGlossaryEntitiesParams) => {
    // Filter selected items into terms and nodes
    const selectedTerms = terms?.filter((term) => selectedItems.includes(term?.urn || '')) || [];
    const selectedNodes = nodes?.filter((node) => selectedItems.includes(node?.urn || '')) || [];

    const totalSelected = selectedTerms.length + selectedNodes.length;

    if (totalSelected === 0) {
        message.info('No glossary entities were selected for deletion.');
        return;
    }

    let confirmMessage = '';
    let titleMessage = 'Delete Glossary Entities';

    if (selectedTerms.length > 0 && selectedNodes.length > 0) {
        confirmMessage = `Are you sure you want to delete ${selectedTerms.length} glossary term(s) and ${selectedNodes.length} term group(s)? This action cannot be undone.`;
    } else if (selectedTerms.length > 0) {
        titleMessage = 'Delete Glossary Terms';
        confirmMessage = `Are you sure you want to delete ${selectedTerms.length} glossary term(s)? This action cannot be undone.`;
    } else {
        titleMessage = 'Delete Term Groups';
        confirmMessage = `Are you sure you want to delete ${selectedNodes.length} term group(s)? This action cannot be undone.`;
    }

    Modal.confirm({
        title: titleMessage,
        content: confirmMessage,
        cancelText: 'No',
        className: 'f-borderless-modal',
        okText: 'Delete',
        style: {
            maxWidth: 400,
        },
        okButtonProps: {
            type: undefined,
            danger: true,
        },
        maskClosable: true,
        closable: true,
        centered: true,
        onOk: async () => {
            setIsDeleting?.(true);

            try {
                // Combine all entities to delete
                const allEntitiesToDelete = [...selectedTerms, ...selectedNodes];

                await batchRemoveGlossaryMutation({
                    variables: {
                        urn: allEntitiesToDelete.map((entity) => entity?.urn || ''),
                    },
                });

                message.success(
                    `Successfully deleted ${totalSelected} glossary ${totalSelected === 1 ? 'entity' : 'entities'}.`,
                );

                // Refetch data to update the UI
                refetch?.();
                onSuccess?.();
            } catch (error: any) {
                message.error(`Failed to delete glossary entities: ${error?.message}`);
            } finally {
                setIsDeleting?.(false);
                setSelectedItems([]);
            }
        },
    });
};
